import { Component, input, Input, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { environment } from '../../../environments/environment';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { EventDateComponent } from '../event-date/event-date.component';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-event-status',
  templateUrl: './event-status.component.html',
  styleUrls: ['./event-status.component.scss'],
  imports: [FontAwesomeModule, EventDateComponent, NgClass],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EventStatusComponent {
  @Input() primaryEventGroup!: string;
  @Input() protestManagementProcess?: boolean;
  @Input() scoreboardStatus?: string;
  @Input() status!: string;
  @Input() date!: string;
  @Input() meetDates!: string[] | undefined;
  @Input() heatDates!: {
    heatNumber: number[];
    name: string;
    date: string;
  }[];
  meetCountryCode = input<string>('US');

  text = '';
  statusClass = '';
  showDate = false;
  icon!: IconProp;

  ngOnInit() {
    this.updateStatus();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes['status'] && !changes['status'].isFirstChange()) ||
      (changes['scoreboardStatus'] && !changes['scoreboardStatus'].isFirstChange())
    ) {
      this.updateStatus();
    }
  }

  protected showDateIfAvailable() {
    this.showDate = !!(this.date && this.meetDates && this.meetDates.length > 0);
  }

  updateStatus() {
    this.showDate = false;
    let status = this.status;
    if (!!this.scoreboardStatus && this.status !== 'r' && this.status !== 'un' && this.status !== 'ca') {
      status = this.scoreboardStatus;
    }

    switch (status) {
      case 'ca': // Cancelled
        this.icon = 'close';
        this.text = $localize`:Short status message to say the event is cancelled@@eventStatusCancelled:Cancelled`;
        this.statusClass = 'status-cancelled';
        break;
      case 'ci': // Checking In
        this.icon = 'door-open';
        this.text = $localize`:Short status message to say the event is available for check in@@eventStatusCheckIn:Check In`;
        //"Multi Table Tab Title on the Event page@@event.multiTableTabTitle"
        // `:meaning|description@@id:source message text`;
        this.statusClass = 'status-checking-in';
        break;
      case 'cc': // Check In Done
        this.icon = 'door-closed';
        this.text = $localize`:Short status message to say that check in for the event is closed@@eventStatusClosedCheckIn:Closed`;
        this.statusClass = 'status-check-in-done';
        break;
      case 'e': // Entries
        this.icon = 'chevron-right';
        this.text = $localize`:Short status message to say the event has entries@@eventStatusEntries:Entries`;
        this.statusClass = 'status-entries';
        this.showDateIfAvailable();
        break;
      case 'eh': // Entries and Heats
        this.icon = 'chevron-right';
        this.text = $localize`:Short status message for events to say the event has seeded start lists @@eventStatus:Start Lists`;
        this.statusClass = 'status-entries-heats';
        this.showDateIfAvailable();
        break;
      case 'h': // Heats
        this.icon = 'chevron-right';
        this.text = $localize`:Short status message for events to say the event has seeded start lists @@eventStatus:Start Lists`;
        this.statusClass = 'status-heats';
        this.showDateIfAvailable();
        break;
      case 'nd': // No data. No entries, heats, or results
        this.icon = 'chevron-right';
        this.text = $localize`:Short status message to say the event is scheduled. This is the default if no other data is available.@@eventStatusScheduled:Scheduled`;
        this.statusClass = 'status-no-data';
        this.showDateIfAvailable();
        break;

      case 'r': // Final results
        this.icon = 'check';
        this.text = this.protestManagementProcess
          ? $localize`:Short status message to say the event has final and official results@@eventStatusOfficial:Official`
          : $localize`:Short status message to say the event has final results@@eventStatusResults:Results`;
        this.statusClass = 'status-results';
        break;
      case 'hr': // Heat results, but not final results
        this.icon = 'ellipsis-h';
        this.text = $localize`:Short status message to say the event is in progress. This appears if the event has heat by heat results uploaded, but is not showing real time results@@eventStatusInProgress:In Progress`;
        this.statusClass = 'status-heat-results';
        break;
      case 'l': // Live. In progress with scoreboard connection
        this.icon = environment.icons.live;
        this.text = $localize`:Short status message to say the event is live right now@@eventStatusLive:Live`;
        // if (runTime) {
        //   this.text = runTime;
        // }
        this.statusClass = 'status-live';
        this.showDateIfAvailable();
        break;
      case 'nl': // No longer live. Waiting state between live results and final results
        this.icon = 'check';
        this.text = $localize`:Short status message to say the event has finished real time results, but no final results have been uploaded@@eventStatusDone:Done`;
        this.statusClass = 'status-no-longer-live';
        break;
      case 'un': // Unofficial. Only used when a timer uses protest management
        this.icon = 'check';
        this.statusClass = 'status-no-longer-live';
        if (this.meetCountryCode()?.toUpperCase() === 'AU') {
          this.text = $localize`:Short status message to say the event has final results@@eventStatusResults:Results`;
        } else {
          this.text = $localize`:Short status message to say the event is finished but only has unofficial results@@eventStatusUnofficial:Unofficial`;
        }
        break;
      case 'up': // Event is under protest
        this.icon = 'exclamation-triangle';
        this.text = $localize`:Short status message to say the event is under protest@@eventStatusUnderProtest:Under Protest`;
        this.statusClass = 'status-under-protest';
        break;
      case 'ur': // Event is under review
        this.icon = 'eye';
        this.text = $localize`:Short status message to say the event is under review@@eventStatusUnderReview:Under Review`;
        this.statusClass = 'status-under-review';
        break;
      case 'pd': // Event is under review
        this.icon = 'ellipsis-h';
        this.text = $localize`:Short status message to say the event is pending@@eventStatusPending:Pending`;
        this.statusClass = 'status-pending';
        break;
      case 'pf': // Event is under review
        this.icon = 'ellipsis-h';
        this.text = $localize`:Short status message to say the event is waiting for the final@@eventStatusPendingFinal:Pending Final`;
        this.statusClass = 'status-pending-final';
        break;
      case 'wu': // Warming Up
        this.icon = 'ellipsis-h';
        this.text = $localize`:Short status message to say the event is warming up@@eventStatusWarmingUp:Warming Up`;
        this.statusClass = 'status-warming-up';
        break;
      default:
        this.icon = 'calendar';
        this.text = $localize`:Short status message to say the event is scheduled. This is the default if no other data is available.@@eventStatusScheduled:Scheduled`;
        this.statusClass = 'status-no-data';
        this.showDateIfAvailable();
        break;
    }
  }
}
